import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';

/**
 * Журнал звонков из CDR Asterisk. Один звонок = одна группа CDR-строк по
 * linkedid, на бэке мы выбираем «главную» строку (с записью / max billsec)
 * и показываем её, плюс по клику — детали со всеми «легами».
 *
 * Прослушивание записи — через прокси-эндпоинт CRM, без раскрытия toкена PBX
 * клиенту, с поддержкой Range-запросов.
 */
const URL_SCHEMA = {
  from: '', to: '', phone: '', disp: '', page: 1,
  open: '', // linkedid открытой деталки
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  return iso.replace('T', ' ').slice(0, 19);
}
function fmtDur(sec) {
  sec = Number(sec) || 0;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m + ':' + String(s).padStart(2, '0');
}
function dispLabel(d) {
  switch (d) {
    case 'ANSWERED':  return { label: 'Принят', cls: 'cd-disp cd-disp--ok' };
    case 'NO ANSWER': return { label: 'Без ответа', cls: 'cd-disp cd-disp--miss' };
    case 'BUSY':      return { label: 'Занято', cls: 'cd-disp cd-disp--miss' };
    case 'FAILED':    return { label: 'Сбой', cls: 'cd-disp cd-disp--err' };
    default:          return { label: d || '—', cls: 'cd-disp' };
  }
}
function dirLabel(d) {
  if (d === 'inbound')  return '↘ Входящий';
  if (d === 'outbound') return '↗ Исходящий';
  return '↔';
}

export default function CallsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canListen = can('telephony.recordings', 'read');

  const [params, setParams] = useUrlState(URL_SCHEMA);

  const [data, setData]       = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const [details, setDetails]     = useState(null);
  const [detLoading, setDetLoad]  = useState(false);
  const [detError, setDetError]   = useState(null);

  const reload = () => {
    setLoading(true); setError(null);
    const qs = new URLSearchParams();
    if (params.from)  qs.set('from', params.from);
    if (params.to)    qs.set('to', params.to);
    if (params.phone) qs.set('phone', params.phone);
    if (params.disp)  qs.set('disposition', params.disp);
    qs.set('page', String(params.page || 1));
    api.get('/api/telephony/calls?' + qs.toString())
      .then((res) => setData({ rows: res.rows || [], total: res.total || 0 }))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [params.from, params.to, params.phone, params.disp, params.page]);

  // Загружаем детали при открытом linkedid
  useEffect(() => {
    if (!params.open) { setDetails(null); return; }
    let cancelled = false;
    setDetLoad(true); setDetError(null);
    api.get('/api/telephony/calls/' + encodeURIComponent(params.open))
      .then((res) => { if (!cancelled) setDetails(res); })
      .catch((e) => { if (!cancelled) setDetError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setDetLoad(false); });
    return () => { cancelled = true; };
  }, [params.open]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / 50));

  return (
    <>
      <Topbar
        breadcrumb={['Администрирование', 'Записи звонков']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>С даты:</label>
          <input
            type="date"
            value={params.from || ''}
            onChange={(e) => setParams({ from: e.target.value, page: 1 })}
            style={{ height: 30 }}
          />
          <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>По:</label>
          <input
            type="date"
            value={params.to || ''}
            onChange={(e) => setParams({ to: e.target.value, page: 1 })}
            style={{ height: 30 }}
          />
          <input
            type="text"
            placeholder="Номер (содержит)"
            value={params.phone || ''}
            onChange={(e) => setParams({ phone: e.target.value, page: 1 })}
            style={{ height: 30, minWidth: 180 }}
          />
          <select
            value={params.disp || ''}
            onChange={(e) => setParams({ disp: e.target.value, page: 1 })}
            style={{ height: 30 }}
          >
            <option value="">Любой статус</option>
            <option value="ANSWERED">Принят</option>
            <option value="NO ANSWER">Без ответа</option>
            <option value="BUSY">Занято</option>
            <option value="FAILED">Сбой</option>
          </select>
          <div className="filterbar__spacer" />
          <span className="filterbar__meta">Найдено: <b>{data.total || 0}</b></span>
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Дата/время</th>
                <th style={{ width: 100 }}>Направление</th>
                <th style={{ width: 160 }}>От</th>
                <th style={{ width: 160 }}>Кому</th>
                <th style={{ width: 90 }}>Длит-ть</th>
                <th style={{ width: 110 }}>Статус</th>
                <th style={{ width: 60 }}>Запись</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && data.rows.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, color: 'var(--fg-muted)', textAlign: 'center' }}>Загрузка…</td></tr>
              )}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, color: 'var(--fg-muted)', textAlign: 'center' }}>Звонков не найдено.</td></tr>
              )}
              {data.rows.map((r) => {
                const d = dispLabel(r.disposition);
                return (
                  <tr key={r.linkedid} onClick={() => setParams({ open: r.linkedid })} style={{ cursor: 'pointer' }}>
                    <td className="mono" style={{ fontSize: 12 }}>{fmtDateTime(r.callDate)}</td>
                    <td>{dirLabel(r.direction)}</td>
                    <td>
                      <PhoneCell phone={r.src} contact={r.srcContact} more={r.srcContactsCount} />
                    </td>
                    <td>
                      <PhoneCell phone={r.dst} contact={r.dstContact} more={r.dstContactsCount} />
                    </td>
                    <td className="mono">{fmtDur(r.billsec)}</td>
                    <td><span className={d.cls}>{d.label}</span></td>
                    <td style={{ textAlign: 'center' }}>
                      {r.recording
                        ? <Icon name="check" size={14} />
                        : <span style={{ color: 'var(--fg-faint)' }}>—</span>}
                    </td>
                    <td className="muted" style={{ fontSize: 11 }}>{r.legs > 1 ? `${r.legs} легов` : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="table-foot">
          <span>Стр. <b>{params.page || 1}</b> из {totalPages}</span>
          <div className="table-foot__pages">
            <button className="pagebtn" disabled={(params.page || 1) <= 1} onClick={() => setParams({ page: (params.page || 1) - 1 })}>‹</button>
            <button className="pagebtn" disabled={(params.page || 1) >= totalPages} onClick={() => setParams({ page: (params.page || 1) + 1 })}>›</button>
          </div>
        </div>
      </div>

      {params.open && (
        <CallDetailModal
          linkedid={params.open}
          details={details}
          loading={detLoading}
          error={detError}
          canListen={canListen}
          onClose={() => setParams({ open: '' })}
        />
      )}
    </>
  );
}

/**
 * Ячейка с номером + (если есть контакт по этому номеру в нашей CRM) —
 * имя контакта мелким шрифтом снизу и метка «+ ещё N», если на этот же
 * номер сохранено больше одного контакта (например, оба родителя).
 */
function PhoneCell({ phone, contact, more }) {
  if (!phone) return <span style={{ color: 'var(--fg-faint)' }}>—</span>;
  return (
    <div style={{ lineHeight: 1.2 }}>
      <div className="mono" style={{ fontSize: 12.5 }}>{phone}</div>
      {contact && (
        <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          {contact.kind === 'student' ? '👤 ' : '☎ '}{contact.name}
          {more > 1 && <span style={{ color: 'var(--fg-faint)' }}> · +ещё {more - 1}</span>}
        </div>
      )}
    </div>
  );
}

function CallDetailModal({ linkedid, details, loading, error, canListen, onClose }) {
  const recUrl = useMemo(() => {
    if (!details?.primary?.recording) return null;
    return '/api/telephony/recording?linkedid=' + encodeURIComponent(linkedid);
  }, [details, linkedid]);

  // Esc + lock scroll
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>
            Звонок
            <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--fg-muted)', fontWeight: 400, fontFamily: 'var(--font-mono)' }}>
              {linkedid}
            </span>
          </h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <div className="sd-edit__body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading && <div className="ref-empty">Загрузка…</div>}
          {error && <div className="sd-edit__error">Ошибка: {error}</div>}
          {details && (
            <>
              {recUrl && canListen && (
                <div>
                  <audio controls preload="metadata" src={recUrl} style={{ width: '100%' }} />
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4 }}>
                    {details.primary?.recordingFile}
                  </div>
                </div>
              )}
              {recUrl && !canListen && (
                <div className="ref-empty" style={{ padding: 12 }}>
                  Нет права на прослушивание записей.
                </div>
              )}

              <div className="pay-section">
                <div className="pay-section__head">
                  <h4>Все строки CDR ({details.legs.length})</h4>
                </div>
                <div className="table-wrap">
                  <table className="table" style={{ fontSize: 11.5 }}>
                    <thead>
                      <tr>
                        <th>Дата</th>
                        <th>App</th>
                        <th>Канал → канал назначения</th>
                        <th>Src → Dst</th>
                        <th>Длит-ть</th>
                        <th>Статус</th>
                        <th>Запись</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.legs.map((leg) => {
                        const d = dispLabel(leg.disposition);
                        return (
                          <tr key={leg.uniqueid}>
                            <td className="mono">{fmtDateTime(leg.calldate)}</td>
                            <td className="muted">{leg.lastapp || '—'}</td>
                            <td className="muted" style={{ fontSize: 10.5 }}>
                              <div>{leg.channel}</div>
                              {leg.dstchannel && <div style={{ color: 'var(--fg-faint)' }}>→ {leg.dstchannel}</div>}
                            </td>
                            <td className="mono">{leg.src} → {leg.dst}</td>
                            <td className="mono">{fmtDur(leg.billsec)}/{fmtDur(leg.duration)}</td>
                            <td><span className={d.cls}>{d.label}</span></td>
                            <td style={{ textAlign: 'center' }}>
                              {leg.recording ? <Icon name="check" size={12} /> : <span style={{ color: 'var(--fg-faint)' }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="sd-edit__foot">
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
